import React, { useEffect, useState } from "react";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Alert, Box, Button } from "@mui/material";
import { publishableKey } from "../utils/stripe-config";
import { formatPrice } from "../utils/utils";

const stripePromise = loadStripe(publishableKey, {
  developerTools: { assistant: { enabled: false } },
});
// Module-level catch so an early load failure doesn't surface as an
// unhandled rejection on routes where this component isn't mounted
// (MATTOBOARD-6Q4). The existing useEffect-level catch on the same promise
// still runs when the component mounts, updating the React state for UX.
stripePromise.catch((err) => {
  console.error("[CreditCheckoutForm] Failed to load Stripe.js:", err);
});

interface CreditCheckoutFormProps {
  clientSecret: string;
  amount: number;
  credits: number;
  currency?: string;
  onSuccess: () => void;
  onError: (error: string) => void;
}

const CheckoutFormInner = ({
  amount,
  credits,
  currency = "usd",
  onSuccess,
  onError,
}: Omit<CreditCheckoutFormProps, "clientSecret">) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setMessage(null);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      setIsProcessing(false);

      if (error) {
        console.error("[CreditCheckoutForm] Payment error:", error);
        const msg = error.message || "Payment failed";
        setMessage(msg);
        onError(msg);
      } else if (paymentIntent?.status === "succeeded") {
        onSuccess();
      } else if (paymentIntent?.status === "processing") {
        const msg = "Payment is processing. Credits will be added once confirmed.";
        setMessage(msg);
        onError(msg);
      } else {
        console.error(
          "[CreditCheckoutForm] Unexpected payment status:",
          paymentIntent?.status
        );
        const msg = "Payment could not be completed. Please try again.";
        setMessage(msg);
        onError(msg);
      }
    } catch (err) {
      console.error("[CreditCheckoutForm] Unexpected error:", err);
      const msg = "An unexpected error occurred";
      setMessage(msg);
      onError(msg);
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement options={{ wallets: { googlePay: 'never', applePay: 'never' } }} />
      <Box sx={{ height: "16px" }} />
      <Button
        type="submit"
        variant="contained"
        disabled={!stripe || isProcessing}
        fullWidth
        sx={{
          fontWeight: 700,
          background: "linear-gradient(135deg, #c58ed4, #a251b9)",
          "&:hover": {
            background: "linear-gradient(135deg, #b57dc3, #9241a8)",
          },
          textTransform: "none",
          borderRadius: "5px",
          fontSize: "15px",
          minHeight: "42px",
          "&:disabled": {
            opacity: 0.6,
          },
        }}
      >
        {isProcessing
          ? "Processing..."
          : `Pay ${formatPrice(amount, currency, true)} for ${credits} Credits`}
      </Button>
      {message && <div style={{ marginTop: '10px', color: '#b42318', fontSize: '14px' }}>{message}</div>}
    </form>
  );
};

export const CreditCheckoutForm = ({
  clientSecret,
  ...props
}: CreditCheckoutFormProps) => {
  const [stripeLoadError, setStripeLoadError] = useState(false);

  useEffect(() => {
    stripePromise.catch((err) => {
      console.error("[CreditCheckoutForm] Failed to load Stripe.js:", err);
      setStripeLoadError(true);
    });
  }, []);

  if (stripeLoadError) {
    return (
      <Alert severity="error">
        Unable to load the payment system. Please disable any ad blockers and
        refresh the page.
      </Alert>
    );
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <CheckoutFormInner {...props} />
    </Elements>
  );
};
